import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import "./BaseSelectModern.css";

/** IDs pueden venir como number (API) y el estado del form como string. */
function normSelectValue(v: string | number | undefined | null): string {
  if (v === undefined || v === null || v === "") return "";
  return String(v);
}

/** Comparación de búsqueda sin acentos (español). */
function normalizeForSearch(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

export type BaseSelectOption = { label: string; value: string | number };

export type BaseSelectModernProps = {
  label: string;
  name: string;
  value: string;
  options: BaseSelectOption[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  preserveCase?: boolean;
  /**
   * Buscador dentro del desplegable.
   * - `true`: siempre visible.
   * - `false`: desactivado.
   * - sin definir: solo si hay al menos `minOptionsForSearch` opciones (def. 8).
   */
  searchable?: boolean;
  /** Umbral para mostrar buscador cuando `searchable` no es true/false explícito. */
  minOptionsForSearch?: number;
  searchPlaceholder?: string;
  /** Oculta el label visualmente (sigue disponible para accesibilidad). */
  hideLabel?: boolean;
  /** Variante compacta para celdas de tabla. */
  size?: "default" | "compact";
  /** Ancho mínimo del panel (útil en celdas estrechas). */
  menuMinWidth?: number;
  /** Si es false, el placeholder solo se muestra en el trigger (no como opción). */
  includePlaceholderOption?: boolean;
  className?: string;
  onOpenChange?: (open: boolean) => void;
  /** Notifica cambios del texto del buscador interno (para búsqueda remota). */
  onSearchChange?: (query: string) => void;
};

const BaseSelectModern: React.FC<BaseSelectModernProps> = ({
  label,
  name,
  value,
  options,
  onChange,
  placeholder = "",
  disabled = false,
  preserveCase = false,
  searchable,
  minOptionsForSearch = 8,
  searchPlaceholder = "Buscar…",
  hideLabel = false,
  size = "default",
  menuMinWidth,
  includePlaceholderOption = true,
  className = "",
  onOpenChange,
  onSearchChange,
}) => {
  const uid = useId();
  const listId = `${uid}-listbox`;
  const searchInputId = `${uid}-search`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [listMaxHeight, setListMaxHeight] = useState<number | undefined>(undefined);

  const fmt = (s: string) => (preserveCase ? s : s.toUpperCase());

  const showSearch =
    searchable !== false &&
    (searchable === true || options.length >= minOptionsForSearch);

  useEffect(() => {
    onSearchChange?.(searchQuery);
  }, [searchQuery, onSearchChange]);

  const rows = useMemo(() => {
    const r: { value: string; label: string }[] = [];
    if (placeholder && includePlaceholderOption) {
      r.push({ value: "", label: placeholder });
    }
    r.push(
      ...options.map((o) => ({
        label: o.label,
        value: normSelectValue(o.value),
      }))
    );
    return r;
  }, [placeholder, options, includePlaceholderOption]);

  const displayRows = useMemo(() => {
    if (!showSearch) return rows;
    const q = searchQuery.trim();
    if (!q) return rows;
    const nq = normalizeForSearch(q);
    return rows.filter((row) => normalizeForSearch(row.label).includes(nq));
  }, [rows, searchQuery, showSearch]);

  const selectedLabel = useMemo(() => {
    const v = normSelectValue(value);
    if (!v) return "";
    const o = options.find((x) => normSelectValue(x.value) === v);
    return o ? fmt(o.label) : "";
  }, [value, options, preserveCase]);

  const emit = useCallback(
    (next: string) => {
      const v = normSelectValue(next);
      onChange({
        target: { name, value: v },
        currentTarget: { name, value: v },
      } as React.ChangeEvent<HTMLSelectElement>);
    },
    [name, onChange]
  );

  /** Altura reservada para la barra de búsqueda dentro del panel. */
  const searchBarReserve = showSearch ? (size === "compact" ? 48 : 56) : 0;

  const setOpenState = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setOpen((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        if (resolved !== prev) onOpenChange?.(resolved);
        return resolved;
      });
    },
    [onOpenChange]
  );

  const positionMenu = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const maxList = size === "compact" ? 260 : 320;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const preferBelow = spaceBelow >= 120 || spaceBelow >= spaceAbove;
    const raw = Math.min(maxList, preferBelow ? spaceBelow : spaceAbove);
    const maxH = Math.max(80, raw - searchBarReserve);
    setListMaxHeight(maxH);

    const preferredWidth = Math.max(r.width, menuMinWidth ?? 0);
    let left = r.left;
    left = Math.min(left, window.innerWidth - preferredWidth - margin);
    left = Math.max(margin, left);

    if (preferBelow) {
      setMenuStyle({
        position: "fixed",
        left,
        width: preferredWidth,
        top: r.bottom + 6,
        bottom: "auto",
        zIndex: 15000,
      });
    } else {
      setMenuStyle({
        position: "fixed",
        left,
        width: preferredWidth,
        top: "auto",
        bottom: window.innerHeight - r.top + 6,
        zIndex: 15000,
      });
    }
  }, [searchBarReserve, menuMinWidth, size]);

  useLayoutEffect(() => {
    if (!open) return;
    positionMenu();
    const raf = requestAnimationFrame(() => positionMenu());
    return () => cancelAnimationFrame(raf);
  }, [open, positionMenu, options.length, showSearch]);

  useLayoutEffect(() => {
    if (!open) return;
    if (showSearch) {
      searchInputRef.current?.focus({ preventScroll: true });
      return;
    }
    menuRef.current?.focus({ preventScroll: true });
  }, [open, showSearch]);

  useEffect(() => {
    if (!open) return;
    setHighlight(displayRows.length > 0 ? 0 : -1);
  }, [searchQuery, open, displayRows.length]);

  useEffect(() => {
    if (!open) setSearchQuery("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpenState(false);
      setHighlight(-1);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenState(false);
        setHighlight(-1);
        triggerRef.current?.focus();
      }
    };
    const onResize = () => {
      positionMenu();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open, positionMenu, setOpenState]);

  const pick = (next: string) => {
    emit(next);
    setOpenState(false);
    setHighlight(-1);
    setSearchQuery("");
    triggerRef.current?.focus();
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpenState(true);
        setHighlight(0);
      }
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (showSearch) return;
    const idx = highlight < 0 ? 0 : highlight;
    const last = Math.max(0, displayRows.length - 1);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight(Math.min(idx + 1, last));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(Math.max(idx - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = displayRows[idx];
      if (o) pick(o.value);
    }
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const last = Math.max(0, displayRows.length - 1);
    const idx = highlight < 0 ? 0 : highlight;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h < 0 ? 0 : Math.min(h + 1, last)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? 0 : h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = displayRows[idx];
      if (row) pick(row.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpenState(false);
      setHighlight(-1);
      triggerRef.current?.focus();
    }
  };

  const triggerId = `${uid}-trigger`;
  const valueNorm = normSelectValue(value);
  const showPlaceholder = !valueNorm;
  const isCompact = size === "compact";

  const menu =
    open &&
    !disabled &&
    createPortal(
      <div
        ref={menuRef}
        id={listId}
        role="listbox"
        className={`base-select-modern__menu ${showSearch ? "base-select-modern__menu--searchable" : ""} ${
          isCompact ? "base-select-modern__menu--compact" : ""
        }`}
        style={menuStyle}
        onKeyDown={onMenuKeyDown}
        tabIndex={showSearch ? -1 : 0}
        onWheel={(e) => {
          e.stopPropagation();
        }}
      >
        {showSearch && (
          <div
            className="base-select-modern__search"
            onMouseDown={(e) => {
              const t = e.target as HTMLElement;
              if (t.closest("input")) return;
              e.preventDefault();
              searchInputRef.current?.focus();
            }}
          >
            <Search
              className="base-select-modern__search-icon"
              size={isCompact ? 16 : 20}
              strokeWidth={2.25}
              aria-hidden
            />
            <input
              id={searchInputId}
              ref={searchInputRef}
              type="text"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              className="base-select-modern__search-input"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              aria-label={searchPlaceholder}
            />
          </div>
        )}
        <ul
          className="base-select-modern__menu-inner"
          role="presentation"
          style={
            listMaxHeight != null
              ? { maxHeight: listMaxHeight, overflowY: "auto" as const }
              : undefined
          }
          onWheel={(e) => e.stopPropagation()}
        >
          {displayRows.length === 0 ? (
            <li className="base-select-modern__empty" role="presentation">
              <span className="base-select-modern__empty-text">Sin coincidencias</span>
            </li>
          ) : (
            displayRows.map((opt, i) => {
              const isPh = Boolean(placeholder && opt.value === "");
              const isSel = valueNorm === opt.value;
              const isHi = highlight === i;
              return (
                <li key={`${opt.value}-${i}`} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    className={`base-select-modern__option ${isSel ? "is-selected" : ""} ${
                      isHi ? "is-highlight" : ""
                    } ${isPh ? "is-placeholder" : ""}`}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(opt.value)}
                  >
                    {fmt(opt.label)}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>,
      document.body
    );

  return (
    <div
      className={`form-group base-select-wrapper base-select-modern ${
        disabled ? "base-select-disabled" : ""
      } ${preserveCase ? "base-select--natural-case" : ""} ${
        isCompact ? "base-select-modern--compact" : ""
      } ${className}`.trim()}
    >
      <label
        className={`base-select-modern__label${hideLabel ? " base-select-modern__label--sr-only" : ""}`}
        htmlFor={triggerId}
      >
        {label}
      </label>
      {/* Select oculto para compatibilidad con envío de formularios HTML */}
      <select
        name={name}
        value={valueNorm}
        onChange={onChange}
        className="base-select-modern__native-hidden"
        aria-hidden
        tabIndex={-1}
        disabled={disabled}
      >
        {placeholder && <option value="">{fmt(placeholder)}</option>}
        {options.map((opt) => {
          const ov = normSelectValue(opt.value);
          return (
            <option key={ov || `opt-${opt.label}`} value={ov}>
              {fmt(opt.label)}
            </option>
          );
        })}
      </select>
      <button
        id={triggerId}
        type="button"
        ref={triggerRef}
        disabled={disabled}
        className={`base-select-modern__trigger ${open ? "is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => {
          if (disabled) return;
          setOpenState((prev) => {
            const next = !prev;
            if (!prev) setHighlight(0);
            return next;
          });
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span
          className={`base-select-modern__value ${showPlaceholder ? "base-select-modern__value--placeholder" : ""}`}
        >
          {selectedLabel || (placeholder ? fmt(placeholder) : "—")}
        </span>
        <ChevronDown
          className={`base-select-modern__chevron ${open ? "is-open" : ""}`}
          size={isCompact ? 16 : 22}
          strokeWidth={2.25}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
};

export default BaseSelectModern;
